/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  IDirectChatState,
  IDirectChatConversation,
  ITenantUser,
  ISendDirectFormInviteDto,
  ISendDirectMeetingMessageDto,
  ISendDirectMessageDto,
  DirectChatStatus,
} from './interface';

export const directChatInitialState: IDirectChatState = {
  filters: {
    keyword: '',
  },
  loading: false,
  conversationLoading: false,
  sendMessageLoading: false,
  sendMeetingMessageLoading: false,
  sendFormInviteLoading: false,
  conversations: [],
  conversationsPage: 1,
  conversationsTotal: 0,
  conversationsLoadingMore: false,
  conversationId: null,
  conversation: {
    _id: '',
    title: '',
    users: [],
    experts: [],
    messages: [],
    createdAt: moment().toISOString(),
    updatedAt: moment().toISOString(),
    directChatStatus: DirectChatStatus.PENDING,
    chatCategory: 'direct',
    unreadMessages: [],
  },
  tenantUsers: [],
  tenantUsersLoading: false,
  tenantUsersPage: 1,
  tenantUsersTotal: 0,
  tenantUsersKeyword: '',
  recentUsers: [],
  recentUsersLoading: false,
};

export const directChatSlice = createSlice({
  name: 'feature/direct-chat',
  initialState: directChatInitialState,
  reducers: {
    setLoading(state: IDirectChatState, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    // --- Recent Users (initial 5) ---
    getRecentUsers(state: IDirectChatState) {
      state.recentUsersLoading = true;
    },
    getRecentUsersSucceeded(
      state: IDirectChatState,
      action: PayloadAction<ITenantUser[]>,
    ) {
      state.recentUsers = action.payload;
      state.recentUsersLoading = false;
    },
    getRecentUsersFailed(state: IDirectChatState) {
      state.recentUsersLoading = false;
    },

    // --- Tenant Users (search only) ---
    getTenantUsers(
      state: IDirectChatState,
      action: PayloadAction<{ keyword?: string } | undefined>,
    ) {
      state.tenantUsersLoading = true;
      state.tenantUsersPage = 1;
      state.tenantUsers = [];
      state.tenantUsersKeyword = action?.payload?.keyword || '';
    },
    loadMoreTenantUsers(state: IDirectChatState) {
      state.tenantUsersLoading = true;
    },
    getTenantUsersSucceeded(
      state: IDirectChatState,
      action: PayloadAction<{ result: ITenantUser[]; total: number }>,
    ) {
      state.tenantUsers = action.payload.result;
      state.tenantUsersTotal = action.payload.total;
      state.tenantUsersPage = 1;
      state.tenantUsersLoading = false;
    },
    appendTenantUsers(
      state: IDirectChatState,
      action: PayloadAction<{ result: ITenantUser[]; total: number; page: number }>,
    ) {
      state.tenantUsers = [...state.tenantUsers, ...action.payload.result];
      state.tenantUsersTotal = action.payload.total;
      state.tenantUsersPage = action.payload.page;
      state.tenantUsersLoading = false;
    },
    getTenantUsersFailed(state: IDirectChatState) {
      state.tenantUsersLoading = false;
    },

    // --- Conversations ---
    getConversations(state: IDirectChatState) {
      state.loading = true;
      state.conversationsPage = 1;
    },
    getConversationsSucceeded(
      state: IDirectChatState,
      action: PayloadAction<{ result: IDirectChatConversation[]; total: number }>,
    ) {
      state.conversations = action.payload.result;
      state.conversationsTotal = action.payload.total;
      state.conversationsPage = 1;
      state.loading = false;
    },
    getConversationsFailed(state: IDirectChatState, action: PayloadAction<string>) {
      state.loading = false;
    },
    loadMoreConversations(state: IDirectChatState) {
      state.conversationsLoadingMore = true;
    },
    appendConversations(
      state: IDirectChatState,
      action: PayloadAction<{ result: IDirectChatConversation[]; total: number; page: number }>,
    ) {
      state.conversations = [...state.conversations, ...action.payload.result];
      state.conversationsTotal = action.payload.total;
      state.conversationsPage = action.payload.page;
      state.conversationsLoadingMore = false;
    },

    // --- Open Conversation (find-or-create) ---
    openConversation(
      state: IDirectChatState,
      action: PayloadAction<{ userId: string }>,
    ) {
      state.conversationLoading = true;
    },
    openConversationSucceeded(
      state: IDirectChatState,
      action: PayloadAction<IDirectChatConversation>,
    ) {
      state.conversationLoading = false;
      state.conversation = action.payload;
      state.conversationId = action.payload._id;
    },
    openConversationFailed(state: IDirectChatState) {
      state.conversationLoading = false;
    },

    // --- Create Conversation ---
    createConversation(
      state: IDirectChatState,
      action: PayloadAction<{ userId: string; resolve: any; reject: any }>,
    ) {
      state.conversationLoading = true;
    },
    createConversationSucceeded(
      state: IDirectChatState,
      action: PayloadAction<IDirectChatConversation>,
    ) {
      state.conversationLoading = false;
      state.conversation = action.payload;
      state.conversationId = action.payload._id;
    },
    createConversationFailed(state: IDirectChatState) {
      state.conversationLoading = false;
    },

    // --- Single Conversation ---
    setConversationId(state: IDirectChatState, action: PayloadAction<string | null>) {
      state.conversationId = action.payload;
    },
    getSingleConversation(state: IDirectChatState) {
      state.conversationLoading = true;
    },
    getSingleConversationSucceeded(
      state: IDirectChatState,
      action: PayloadAction<IDirectChatConversation>,
    ) {
      state.conversation = action.payload;
      state.conversationId = action.payload._id;
      state.conversationLoading = false;
    },
    getSingleConversationFailed(state: IDirectChatState, action: PayloadAction<string>) {
      state.conversationLoading = false;
    },

    // --- Send Message ---
    sendMessage(state: IDirectChatState, action: PayloadAction<ISendDirectMessageDto>) {
      state.sendMessageLoading = true;
    },
    sendMessageSucceeded(state: IDirectChatState) {
      state.sendMessageLoading = false;
    },
    sendMessageFailed(state: IDirectChatState, action: PayloadAction<string>) {
      state.sendMessageLoading = false;
    },
    sendMeetingMessage(
      state: IDirectChatState,
      action: PayloadAction<ISendDirectMeetingMessageDto>,
    ) {
      state.sendMeetingMessageLoading = true;
    },
    sendMeetingMessageSucceeded(state: IDirectChatState) {
      state.sendMeetingMessageLoading = false;
    },
    sendMeetingMessageFailed(state: IDirectChatState, action: PayloadAction<string>) {
      state.sendMeetingMessageLoading = false;
    },

    sendFormInvite(state: IDirectChatState, action: PayloadAction<ISendDirectFormInviteDto>) {
      state.sendFormInviteLoading = true;
    },
    sendFormInviteSucceeded(state: IDirectChatState) {
      state.sendFormInviteLoading = false;
    },
    sendFormInviteFailed(state: IDirectChatState, action: PayloadAction<string>) {
      state.sendFormInviteLoading = false;
    },

    // --- Read All ---
    readAllMessages(state: IDirectChatState) {},

    // --- Filters ---
    setKeyword(state: IDirectChatState, action: PayloadAction<string>) {
      state.filters.keyword = action.payload;
    },
    clearFilters(state: IDirectChatState) {
      state.filters = directChatInitialState.filters;
    },

    // --- Reset ---
    resetState() {
      return directChatInitialState;
    },
  },
});

export const directChatActions = directChatSlice.actions;
export default directChatSlice;
